import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BsFillTrash3Fill } from "react-icons/bs";
import { _permissions_all } from "assets/permissions";
import { SiteFeatures } from "config/siteFeatures";

// LiveCasino bölümü için provider key formatı: "originalName" veya "originalName_middleware"
export const parseProviderKey = (key) => {
  let originalName = key;
  let middleware = "";
  if (key.includes("_")) {
    const parts = key.split("_");
    const possibleMiddleware = parts[parts.length - 1];
    const commonMiddlewares = ["matgame", "innova", "blas975", "gasea"];
    if (commonMiddlewares.includes(possibleMiddleware)) {
      originalName = parts.slice(0, -1).join("_");
      middleware = possibleMiddleware;
    }
  }
  return { originalName, middleware, fullKey: key };
};

// SettingName formatı gerçek asset JSON'undan (livecasino.json) doğrulandı:
// dash ("-") korunur (ör. "INNOVA_MICRO-GAMING-LIVE_LIVE_CASINO_ACCESS").
export const createPermissionKey = (providerValue, _middleware) => {
  let permissionKey = providerValue
    .toUpperCase()
    .replace(/\s/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");

  let _middlewareKey = _middleware
    .toUpperCase()
    .replace(/-/g, "_")
    .replace(/\s/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");

  if (_middleware == "gasea" || _middleware == "nonameHero") {
    permissionKey = `${permissionKey}_LIVE_CASINO_ACCESS`;
  } else {
    permissionKey = `${_middlewareKey}_${permissionKey}_LIVE_CASINO_ACCESS`;
  }

  return permissionKey;
};

// İstenen görüntülenme sırası (yetki kontrolünden SONRA uygulanır). Bazı provider'lar
// middleware'e göre farklı vendor kodlarıyla geliyor (ör. innova'da "eclipse-b",
// "skywind-live", gasea'da "GASEA_Evolution_Live"), bu yüzden eşleştirme normalize
// edilmiş isimle "startsWith" üzerinden yapılıyor (bkz. findLiveCasinoPriorityIndex).
const liveCasinoPriorityProviders = [
  "pragmatic",
  "evolution",
  "ezugi",
  "popok",
  "amusnet",
  "eclipse",
  "skywind",
  "creedroomz",
];

// Aynı provider birden fazla middleware'de bulunuyorsa (ör. hem matgame hem innova
// altında "evolution") bu sıraya göre önceliklendirilir. Casino/FilterSearch.jsx ile
// AYNI middleware sırası kullanılıyor.
const liveCasinoMiddlewareOrder = {
  nonameHero: 1,
  matgame: 2,
  innova: 3,
  gasea: 4,
};

const normalizeLiveProviderName = (name) =>
  (name || "").toLowerCase().replace(/[-_\s]/g, "");

// nonameHero ve gasea, games objesinde provider adını "NONAMEHERO_"/"GASEA_" öneki İLE
// BİRLİKTE saklıyor (bu önek createPermissionKey'de kasıtlı olarak korunuyor). Bu önek
// yüzünden "GASEA_Evolution_Live" gibi bir isim normalize edildiğinde "evolution" ile
// başlamıyordu - bu yüzden eşleştirme öncesi sadece burada, GÖRÜNTÜLEME SIRASI amacıyla
// önek temizleniyor (Casino/FilterSearch.jsx ile aynı yaklaşım).
const stripKnownLiveProviderPrefix = (name) =>
  (name || "").replace(/^(NONAMEHERO_|GASEA_)/i, "");

const findLiveCasinoPriorityIndex = (originalName) => {
  const normalized = normalizeLiveProviderName(
    stripKnownLiveProviderPrefix(originalName),
  );
  return liveCasinoPriorityProviders.findIndex((p) =>
    normalized.startsWith(normalizeLiveProviderName(p)),
  );
};

// Bir provider key'inin LiveCasino listesinde gösterilmesi VE oynatılması için
// gereken tek doğruluk kaynağı: içerik kriteri (categoryCode/subtype/game_type) + izin bayrağı.
export const isProviderKeyAllowed = (
  providerKey,
  games,
  permissions,
  loggedIn,
) => {
  if (!games) return false;
  const providerGames = games[providerKey];
  if (
    !providerGames ||
    !Array.isArray(providerGames) ||
    providerGames.length === 0
  ) {
    return false;
  }

  const parsed = parseProviderKey(providerKey);
  const originalProviderName = parsed.originalName;
  const middleware = parsed.middleware || providerGames[0]._midleware || "";
  const permissionKey = createPermissionKey(originalProviderName, middleware);
  const permissionEntry = permissions?.[permissionKey];
  const isGuestPreview = !SiteFeatures.CLOSED_SYSTEM && !loggedIn;

  if (providerGames.some((x) => x._midleware == "gasea")) {
    return (
      (isGuestPreview || permissionEntry?.Value) &&
      (providerGames.some((game) => game.categoryCode === "POKER") ||
        providerGames.some((game) => game.categoryCode === "LIVE") ||
        providerGames.some((game) => game.categoryCode === "LIVEGRAND"))
    );
  }

  if (providerGames.some((x) => x._midleware == "innova")) {
    return (
      (isGuestPreview || permissionEntry?.Value) &&
      providerGames.some((game) => game.subtype === "live-casino") &&
      providerGames.some((game) => game.enabled === 1)
    );
  }

  return (
    (isGuestPreview || permissionEntry?.Value) &&
    providerGames.some((game) => game.game_type === 2) &&
    providerGames.some((game) => game.is_active === 1)
  );
};

export const getAllowedProviderKeys = (games, permissions, loggedIn) => {
  if (!games) return [];
  return Object.keys(games).filter((providerKey) =>
    isProviderKeyAllowed(providerKey, games, permissions, loggedIn),
  );
};

export const sortProviderKeys = (keys, games) => {
  return [...keys].sort((a, b) => {
    const aParsed = parseProviderKey(a);
    const bParsed = parseProviderKey(b);

    const aGames = games[a];
    const bGames = games[b];
    const aMiddleware =
      aGames && aGames.length > 0
        ? aGames[0]._midleware
        : aParsed.middleware || "";
    const bMiddleware =
      bGames && bGames.length > 0
        ? bGames[0]._midleware
        : bParsed.middleware || "";

    const aIndex = findLiveCasinoPriorityIndex(aParsed.originalName);
    const bIndex = findLiveCasinoPriorityIndex(bParsed.originalName);

    // 1) Öncelik listesindeki provider'lar, verilen sıraya göre en önde.
    if (aIndex !== -1 && bIndex !== -1) {
      if (aIndex !== bIndex) return aIndex - bIndex;
      // Aynı provider farklı middleware'lerde varsa: nonameHero > matgame > innova > gasea
      const aOrder = liveCasinoMiddlewareOrder[aMiddleware] || 99;
      const bOrder = liveCasinoMiddlewareOrder[bMiddleware] || 99;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return aParsed.originalName.localeCompare(bParsed.originalName);
    }
    if (aIndex !== -1 && bIndex === -1) return -1;
    if (aIndex === -1 && bIndex !== -1) return 1;

    // 2) Öncelik listesinde olmayanlar: önce middleware sırası, sonra eski
    // _permissions_all sırası, en son alfabetik.
    const aOrder = liveCasinoMiddlewareOrder[aMiddleware] || 99;
    const bOrder = liveCasinoMiddlewareOrder[bMiddleware] || 99;
    if (aOrder !== bOrder) return aOrder - bOrder;

    const aPermissionIndex = _permissions_all.findIndex((p) => {
      if (aMiddleware) {
        return (
          p.provider === aParsed.originalName && p.middleware === aMiddleware
        );
      }
      return p.provider === aParsed.originalName;
    });

    const bPermissionIndex = _permissions_all.findIndex((p) => {
      if (bMiddleware) {
        return (
          p.provider === bParsed.originalName && p.middleware === bMiddleware
        );
      }
      return p.provider === bParsed.originalName;
    });

    if (aPermissionIndex !== -1 && bPermissionIndex !== -1)
      return aPermissionIndex - bPermissionIndex;
    if (aPermissionIndex !== -1 && bPermissionIndex === -1) return -1;
    if (aPermissionIndex === -1 && bPermissionIndex !== -1) return 1;

    return aParsed.originalName.localeCompare(bParsed.originalName);
  });
};

// Login olmayan (misafir) kullanıcı için, aynı provider'ın farklı middleware'lerdeki
// kopyaları karışıklık yaratmasın diye listede sadece 1 kez gösterilir. Sorun şu ki
// middleware'ler arasında aynı provider FARKLI ham isimlerle gelebiliyor (ör. innova'da
// "eclipse-b", "skywind-live", matgame'de "eclipse"), yani normalize edilmiş isimler
// birebir eşleşmeyebiliyor. Bu yüzden dedupe anahtarı olarak findLiveCasinoPriorityIndex'in
// kullandığı AYNI "startsWith" eşleştirmesi kullanılıyor: öncelik listesindeki bir
// provider'a eşleşen her varyant aynı kanonik isme indirgenir. Öncelik listesinde
// olmayan provider'lar için eski davranışa (normalize edilmiş isim) düşülür.
const getLiveCasinoProviderDedupeKey = (originalName) => {
  const priorityIndex = findLiveCasinoPriorityIndex(originalName);
  if (priorityIndex !== -1) return liveCasinoPriorityProviders[priorityIndex];
  return normalizeLiveProviderName(stripKnownLiveProviderPrefix(originalName));
};

export const dedupeProviderKeysByProvider = (keys) => {
  const seen = new Set();
  const result = [];
  for (const key of keys) {
    const { originalName } = parseProviderKey(key);
    const dedupeKey = getLiveCasinoProviderDedupeKey(originalName);
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    result.push(key);
  }
  return result;
};

export const getSortedAllowedProviderKeys = (games, permissions, loggedIn) => {
  const sorted = sortProviderKeys(
    getAllowedProviderKeys(games, permissions, loggedIn),
    games,
  );
  return loggedIn ? sorted : dedupeProviderKeysByProvider(sorted);
};

const FilterSearch = (props) => {
  const { t } = useTranslation();
  const [searchShow, setSearchShow] = useState(false);
  const [providerModalShow, setProviderModalShow] = useState(false);
  const [providerSearchValue, setProviderSearchValue] = useState("");
  const [localSelectedProviders, setLocalSelectedProviders] = useState([]);
  const [filterButton1, setFilterButton1] = useState(false); // İlk filtre butonu
  const [filterButton2, setFilterButton2] = useState(false); // İkinci filtre butonu
  const {
    FilterSearchValue,
    setFilterSearchValue,
    games,
    setProvider,
    selectedProviders,
    setSelectedProviders,
    onFilterButtonsChange,
  } = props;

  // Mobil cihazlarda klavye açıldığında scroll kaymasını önle
  useEffect(() => {
    if (!searchShow) return;

    let initialScrollY = window.scrollY;
    let initialViewportHeight =
      window.visualViewport?.height || window.innerHeight;

    const handleViewportResize = () => {
      // Viewport height değiştiğinde (klavye açıldığında) scroll pozisyonunu koru
      const currentViewportHeight =
        window.visualViewport?.height || window.innerHeight;

      if (currentViewportHeight < initialViewportHeight) {
        // Klavye açıldı, scroll pozisyonunu koru
        requestAnimationFrame(() => {
          window.scrollTo({
            top: initialScrollY,
            behavior: "instant",
          });
        });
      }
    };

    const handleScroll = () => {
      // Kullanıcı manuel scroll yaptığında initial pozisyonu güncelle
      if (window.scrollY !== initialScrollY) {
        initialScrollY = window.scrollY;
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleViewportResize);
    } else {
      window.addEventListener("resize", handleViewportResize);
    }

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          handleViewportResize,
        );
      } else {
        window.removeEventListener("resize", handleViewportResize);
      }
      window.removeEventListener("scroll", handleScroll);
    };
  }, [searchShow]);

  const handleCloseSearch = () => {
    setSearchShow(false);
    if (setFilterSearchValue) setFilterSearchValue("");
  };

  // Filtre buton durumlarını parent component'e bildir
  useEffect(() => {
    if (onFilterButtonsChange) {
      // Hangi butonun aktif olduğunu gönder: 0 = hiçbiri, 1 = buton1, 2 = buton2
      if (filterButton1) {
        onFilterButtonsChange(1);
      } else if (filterButton2) {
        onFilterButtonsChange(2);
      } else {
        onFilterButtonsChange(0);
      }
    }
  }, [filterButton1, filterButton2, onFilterButtonsChange]);

  const handleProviderSelect = (providerName) => {
    if (setProvider) {
      setProvider(providerName);
    }
    setProviderModalShow(false);
    setProviderSearchValue("");
  };

  const handleProviderToggle = (providerName) => {
    setLocalSelectedProviders((prev) => {
      if (prev.includes(providerName)) {
        return prev.filter((p) => p !== providerName);
      } else {
        return [...prev, providerName];
      }
    });
  };

  const handleSelectAll = () => {
    const allProviders = getProviderNames();
    setLocalSelectedProviders(allProviders);
  };

  const handleDeselectAll = () => {
    setLocalSelectedProviders([]);
  };

  const handleApplySelection = () => {
    if (setSelectedProviders && localSelectedProviders.length > 0) {
      // Çoklu provider seçimini üst bileşene gönder
      setSelectedProviders(localSelectedProviders);
    }
    setProviderModalShow(false);
    setProviderSearchValue("");
  };

  const handleCloseProviderModal = () => {
    setProviderModalShow(false);
    setProviderSearchValue("");
    // Local state'i temizleme, sadece modal'ı kapat
  };

  // Provider listesi artık tek bir doğruluk kaynağından geliyor: isProviderKeyAllowed.
  // LiveCasino/index.jsx'teki gerçek oyun listesi de aynı fonksiyonu kullanıyor, böylece
  // modal ile asıl liste arasında tutarsızlık oluşmaz.
  const getProviderNames = () => {
    if (!games) return [];
    return getAllowedProviderKeys(
      games,
      props.permissions,
      props.loggedIn,
    ).filter((providerKey) => {
      const { originalName } = parseProviderKey(providerKey);
      return originalName
        .toLowerCase()
        .includes(providerSearchValue.toLowerCase());
    });
  };

  // Sağlayıcı ismini daha okunabilir hale getir
  const formatProviderName = (name) => {
    return name
      .replace(/-/g, " ")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase())
      .replace(/fundist/g, "")
      .trim();
  };

  return (
    <div className="px-2 w-full">
      <div className="flex flex-row  h-[46px] mb-2 mt-2 items-center gap-2 relative">
        {/* Sol taraf - Provider seçim butonu */}
        <div
          className={`transition-all duration-300 ease-in-out ${searchShow ? "w-0 opacity-0 overflow-hidden" : "flex-shrink-0"}`}
        >
          <button
            onClick={() => setProviderModalShow(true)}
            className="h-[40px] bg-transparent filterInputIcon border border-white/20 rounded-lg flex items-center gap-2 px-3 text-gray-300 whitespace-nowrap"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z"
              />
            </svg>
            <span className="text-sm">{t("BUTTON_SELECT_PROVIDER")}</span>
          </button>
        </div>

        {/* Search box - Tam genişlik */}
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            searchShow ? "flex-1 opacity-100" : "w-full opacity-0 "
          }`}
        >
          <div
            className="h-[40px] bg-gray-800 rounded-lg flex items-center gap-2 px-3 relative"
            style={{ position: "relative", zIndex: 1 }}
          >
            <input
              value={FilterSearchValue}
              onChange={(e) => {
                if (setFilterSearchValue) setFilterSearchValue(e.target.value);
              }}
              onFocus={(e) => {
                // Mobil cihazlarda klavye açıldığında scroll kaymasını önle
                const currentScrollY = window.scrollY;
                // Kısa bir gecikme ile scroll pozisyonunu koru
                setTimeout(() => {
                  window.scrollTo({
                    top: currentScrollY,
                    behavior: "instant",
                  });
                }, 150);
              }}
              type="text"
              className="flex-1 bg-transparent text-white placeholder-gray-400 outline-none"
              placeholder="Oyun Ara"
              autoFocus={searchShow}
            />

            {/* Clear button - Her zaman görünür ve search box'ı kapatır */}
            <button
              onClick={handleCloseSearch}
              className="text-gray-400 hover:text-white"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18 18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Sağ taraf - Mercek ikonu ve Grid butonları */}
        <div
          className={`transition-all duration-300 ease-in-out ${searchShow ? "w-0 opacity-0 overflow-hidden" : "flex-shrink-0 flex flex-row gap-1"}`}
        >
          <button
            onClick={() => {
              // Eğer zaten aktifse pasif yap, değilse aktif yap ve diğerini pasif yap
              if (filterButton1) {
                setFilterButton1(false);
              } else {
                setFilterButton1(true);
                setFilterButton2(false);
              }
            }}
            className={`w-[40px] h-[40px] bg-transparent filterInputIcon border rounded-lg flex items-center justify-center transition-colors ${
              filterButton1
                ? "border-blue-500 bg-blue-500/20"
                : "border-white/20"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className={`w-5 h-5 ${filterButton1 ? "text-blue-400" : "text-white"}`}
            >
              {/* 2 sütunlu grid icon */}
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z"
              />
            </svg>
          </button>
          <button
            onClick={() => {
              // Eğer zaten aktifse pasif yap, değilse aktif yap ve diğerini pasif yap
              if (filterButton2) {
                setFilterButton2(false);
              } else {
                setFilterButton2(true);
                setFilterButton1(false);
              }
            }}
            className={`w-[40px] h-[40px] bg-transparent filterInputIcon border rounded-lg flex items-center justify-center transition-colors ${
              filterButton2
                ? "border-blue-500 bg-blue-500/20"
                : "border-white/20"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className={`w-5 h-5 ${filterButton2 ? "text-blue-400" : "text-white"}`}
            >
              {/* 3 sütunlu grid icon */}
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 3h5v5H3V3zm6 0h5v5H9V3zm6 0h5v5h-5V3zM3 10h5v5H3v-5zm6 0h5v5H9v-5zm6 0h5v5h-5v-5z"
              />
            </svg>
          </button>
          <button
            onClick={() => setSearchShow(!searchShow)}
            className="w-[40px] h-[40px] bg-transparent filterInputIcon border border-white/20 rounded-lg flex items-center justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5 text-white"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Sağlayıcı Seçim Modal'ı */}
      {providerModalShow &&
        createPortal(
          <div
            className="fixed inset-0 bg-secondary-500 z-[9999] flex flex-col"
            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
          >
            <div className="w-full h-full flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-8 border-b border-gray-700">
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleCloseProviderModal}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-8 h-8"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 19.5 8.25 12l7.5-7.5"
                      />
                    </svg>
                  </button>
                  <div>
                    <span className="text-white font-bold text-2xl">
                      {t("FILTER_PROVIDER_SELECT")}
                    </span>
                    {true && (
                      <div className="text-blue-400 text-sm mt-1">
                        {t("FILTER_PROVIDER_SELECTED", {
                          count: localSelectedProviders.length,
                        })}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  className="bg-transparent border border-white/20 rounded-lg p-2"
                  onClick={handleDeselectAll}
                >
                  <BsFillTrash3Fill size={20} />
                </button>
              </div>

              {/* Arama Kutusu */}
              <div className="p-8 border-b border-gray-700">
                <div className="relative">
                  <input
                    type="text"
                    value={providerSearchValue}
                    onChange={(e) => setProviderSearchValue(e.target.value)}
                    placeholder={t("FILTER_PROVIDER_SEARCH")}
                    className="w-full bg-secondary-50/10 border border-secondary-50/20 border rounded-lg px-6 py-4 pl-14 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 text-md"
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 text-gray-400 absolute left-5 top-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                    />
                  </svg>
                </div>
              </div>

              {/* Sağlayıcı Listesi */}
              <div className="flex-1 overflow-y-auto p-2">
                <div className="grid grid-cols-4 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                  {(props.loggedIn
                    ? sortProviderKeys(getProviderNames(), games)
                    : dedupeProviderKeysByProvider(
                        sortProviderKeys(getProviderNames(), games),
                      )
                  ).map((providerKey, index) => {
                    const originalProviderName =
                      parseProviderKey(providerKey).originalName;

                    return (
                      <div
                        key={index}
                        onClick={() => handleProviderToggle(providerKey)}
                        className={`flex flex-col justify-center items-center min-h-[72px] h-auto py-2 px-1 relative border rounded-lg text-left  transition-all duration-200 transform hover:scale-105 cursor-pointer overflow-hidden ${
                          localSelectedProviders.includes(providerKey)
                            ? " bg-gray-700 border-gray-500"
                            : " bg-secondary-50/10 border-secondary-50/20"
                        }`}
                      >
                        {/* Checkbox */}
                        <div className="absolute top-1 right-1 opacity-0">
                          <div
                            className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                              localSelectedProviders.includes(providerKey)
                                ? "bg-blue-500 border-blue-500"
                                : "border-gray-400"
                            }`}
                          >
                            {localSelectedProviders.includes(providerKey) && (
                              <svg
                                className="w-3 h-3 text-white"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>
                        </div>

                        {/*
                         <div className="text-white text-[11px] font-bold text-center break-all leading-tight px-1 w-full max-w-full" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                          {providerName
                            .replace(/-/g, ' ')
                            .replace(/_/g, ' ')
                            .toUpperCase()}
                        </div>
                        */}

                        <div className="text-white text-base font-medium truncate text-center">
                          <img
                            src={ProviderLogo[originalProviderName]}
                            alt={originalProviderName}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-8 border-t border-gray-700">
                <div className="flex gap-4">
                  <button
                    onClick={handleApplySelection}
                    disabled={localSelectedProviders.length === 0}
                    className={`flex-1 py-4 rounded-lg font-bold text-md transition-colors ${
                      localSelectedProviders.length > 0
                        ? "bg-white text-gray-900 hover:bg-gray-100"
                        : "bg-gray-500 text-gray-300 cursor-not-allowed"
                    }`}
                  >
                    {t("FILTER_BUTTON_APPLY", {
                      count: localSelectedProviders.length,
                    })}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

const mapStateToProps = (state, props) => {
  const { authentication, game } = state;
  const { user, loggedIn, permissions, permissionsAll } = authentication;
  const { sports, games, liveGames } = game;
  return {
    user,
    games,
    permissions,
    permissionsAll,
    loggedIn,
  };
};
const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(FilterSearch);

const mergeProviders = [];

const ProviderLogo = {
  pragmatic: require("../../../assets/img/catalog/fortuna45/pragmatic.png"),
  evoplay: require("../../../assets/img/catalog/fortuna45/evoplay.png"),
  egt_digital: require("../../../assets/img/catalog/fortuna45/egt.png"),
  greentube: require("../../../assets/img/catalog/fortuna45/greentube.png"),
  habanero: require("../../../assets/img/catalog/fortuna45/habanero.png"),
  gaminator: require("../../../assets/img/catalog/fortuna45/gaminator.png"),
  apollo_games: require("../../../assets/img/catalog/fortuna45/apollo_games.png"),
  spribe: require("../../../assets/img/catalog/fortuna45/spribe.png"),
  blueprint_gaming: require("../../../assets/img/catalog/fortuna45/blueprint_gaming.png"),
  playson: require("../../../assets/img/catalog/fortuna45/playson.png"),
  betsoft: require("../../../assets/img/catalog/fortuna45/betsoft.png"),
  scientific_games: require("../../../assets/img/catalog/fortuna45/scientific_games.png"),
  evolution: require("../../../assets/img/catalog/fortuna45/evolution_gaming.png"),
  egt: require("../../../assets/img/catalog/fortuna45/egt.png"),
  merkur: require("../../../assets/img/catalog/fortuna45/merkur.png"),
  novomatic: require("../../../assets/img/catalog/fortuna45/novomatic.png"),
  booongo: require("../../../assets/img/catalog/fortuna45/booongo.png"),
  ezugi: require("../../../assets/img/catalog/fortuna45/ezugi.png"),
  aviatrix: require("../../../assets/img/catalog/fortuna45/aviatrix.png"),
  yeebet: require("../../../assets/img/catalog/fortuna45/yeebet.png"),
  ailivecasino: require("../../../assets/img/catalog/fortuna45/ailivecasino.png"),
  fatpanda: require("../../../assets/img/catalog/fortuna45/fatpanda.png"),
  pgsoft: require("../../../assets/img/catalog/fortuna45/pgsoft.png"),
  reelkingdom: require("../../../assets/img/catalog/fortuna45/reelkingdom.png"),
  toptrendgaming: require("../../../assets/img/catalog/fortuna45/toptrendgaming.png"),
  goldenrace: require("../../../assets/img/catalog/fortuna45/goldenrace.png"),
  dreamgaming: require("../../../assets/img/catalog/fortuna45/dreamgaming.png"),
  worldentertainment: require("../../../assets/img/catalog/fortuna45/worldentertainment.png"),
  awc: require("../../../assets/img/catalog/fortuna45/awc.png"),
  "ct855(k0)": require("../../../assets/img/catalog/fortuna45/ct855.png"),
  wmcasino: require("../../../assets/img/catalog/fortuna45/wmcasino.png"),
  vivo: require("../../../assets/img/catalog/fortuna45/vivo.png"),
  portomaso: require("../../../assets/img/catalog/fortuna45/portomaso.png"),
  quickspin: require("../../../assets/img/catalog/fortuna45/quickspin.png"),
  btg: require("../../../assets/img/catalog/fortuna45/btg.png"),
  konami: require("../../../assets/img/catalog/fortuna45/konami.png"),

  wmg: require("../../../assets/img/catalog/fortuna45/wmg.png"),
  microgaming: require("../../../assets/img/catalog/fortuna45/microgaming.png"),
  n2: require("../../../assets/img/catalog/fortuna45/n2.png"),
  apollo: require("../../../assets/img/catalog/fortuna45/apollo.png"),
  kajot: require("../../../assets/img/catalog/fortuna45/kajot.png"),
  jetx: require("../../../assets/img/catalog/fortuna45/jetx.png"),
  fachai: require("../../../assets/img/catalog/fortuna45/fachai.png"),
  mrslotty: require("../../../assets/img/catalog/fortuna45/mrslotty.png"),
  bigpot: require("../../../assets/img/catalog/fortuna45/bigpot.png"),
  epicwin: require("../../../assets/img/catalog/fortuna45/epicwin.png"),
  kagaming: require("../../../assets/img/catalog/fortuna45/kagaming.png"),
  rich88: require("../../../assets/img/catalog/fortuna45/rich88.png"),
  igt: require("../../../assets/img/catalog/fortuna45/igt.png"),
  jili: require("../../../assets/img/catalog/fortuna45/jili.png"),
  cq9: require("../../../assets/img/catalog/fortuna45/cq9.png"),
  live22: require("../../../assets/img/catalog/fortuna45/live22.png"),
  spadegaming: require("../../../assets/img/catalog/fortuna45/spadegaming.png"),
  7777: require("../../../assets/img/catalog/fortuna45/7777Gaming.png"),
  "1x2gaming": require("../../../assets/img/catalog/fortuna45/1x2Gaming.png"),
  "3oaks": require("../../../assets/img/catalog/fortuna45/3Oaks_3.png"),
  "3oaksP": require("../../../assets/img/catalog/fortuna45/3Oaks_3.png"),
  "7-mojos-slots": require("../../../assets/img/catalog/fortuna45/7mojosGaming.png"),
  airdice: require("../../../assets/img/catalog/fortuna45/AirDiceGaming.png"),
  altente: require("../../../assets/img/catalog/fortuna45/AltenteGaming.png"),
  amigogaming: require("../../../assets/img/catalog/fortuna45/AmigoGaming.png"),
  amusnet: require("../../../assets/img/catalog/fortuna45/AmusnetGaming.png"),
  apparat: require("../../../assets/img/catalog/fortuna45/ApparatGaming.png"),
  armadillostudios: require("../../../assets/img/catalog/fortuna45/ArmadilloGaming.png"),

  backseat: require("../../../assets/img/catalog/fortuna45/BackseatGaming.png"),
  "barbara-bang": require("../../../assets/img/catalog/fortuna45/BarbarabangGaming.png"),
  "betsoft-a": require("../../../assets/img/catalog/fortuna45/BetSoftGaming.png"),
  bfgames: require("../../../assets/img/catalog/fortuna45/BFGamesGaming.png"),
  bfgamesB: require("../../../assets/img/catalog/fortuna45/BFGamesGaming.png"),
  bgaming: require("../../../assets/img/catalog/fortuna45/BGamingGaming.png"),
  bgamingP: require("../../../assets/img/catalog/fortuna45/BGamingGaming.png"),
  "bigtimegaming-gh": require("../../../assets/img/catalog/fortuna45/BigTimeGaming.png"),
  booming: require("../../../assets/img/catalog/fortuna45/BoomingGaming.png"),

  boominggames: require("../../../assets/img/catalog/fortuna45/BoomingGaming.png"),

  bullshark: require("../../../assets/img/catalog/fortuna45/BullsharkGaming.png"),
  bwgaming: require("../../../assets/img/catalog/fortuna45/BWGaming.png"),
  caletagaming: require("../../../assets/img/catalog/fortuna45/CaletaGaming.png"),
  clawbuster: require("../../../assets/img/catalog/fortuna45/ClawbusterGaming.png"),
  ctinteractive: require("../../../assets/img/catalog/fortuna45/CT_InteractiveGaming.png"),
  dreamplay: require("../../../assets/img/catalog/fortuna45/DreamPlayGaming.png"),
  eagaming: require("../../../assets/img/catalog/fortuna45/EAGaming.png"),
  "elagames-a": require("../../../assets/img/catalog/fortuna45/ElaGamesGaming.png"),
  endorphina: require("../../../assets/img/catalog/fortuna45/EndorphinaGaming.png"),
  fazi: require("../../../assets/img/catalog/fortuna45/FaziGaming.png"),
  fils: require("../../../assets/img/catalog/fortuna45/FilsGameGaming.png"),
  flipluck: require("../../../assets/img/catalog/fortuna45/FlipLuckGaming.png"),
  formulaspin: require("../../../assets/img/catalog/fortuna45/FormulaSpinGaming.png"),
  galaxsys: require("../../../assets/img/catalog/fortuna45/GalaxsysGaming.png"),
  gamebeat: require("../../../assets/img/catalog/fortuna45/GameBeatGaming.png"),
  gamzix: require("../../../assets/img/catalog/fortuna45/GamixGaming.png"),
  hacksaw: require("../../../assets/img/catalog/fortuna45/HacksawGaming.png"),
  "iconic21-slots": require("../../../assets/img/catalog/fortuna45/Iconic21Gaming.png"),
  iconix: require("../../../assets/img/catalog/fortuna45/IconixViatorGaming.png"),

  irondog: require("../../../assets/img/catalog/fortuna45/IronDogGaming.png"),
  irondogP: require("../../../assets/img/catalog/fortuna45/IronDogGaming.png"),
  irondogPP: require("../../../assets/img/catalog/fortuna45/IronDogGaming.png"),
  jvl: require("../../../assets/img/catalog/fortuna45/JVLGaming.png"),
  macaw: require("../../../assets/img/catalog/fortuna45/MacawGaming.png"),
  mancala: require("../../../assets/img/catalog/fortuna45/MancalaGaming.png"),
  "micro-gaming": require("../../../assets/img/catalog/fortuna45/microgaming.png"),
  netent: require("../../../assets/img/catalog/fortuna45/NetEntGaming.png"),
  "netent-gh": require("../../../assets/img/catalog/fortuna45/NetEntGaming.png"),
  netgame: require("../../../assets/img/catalog/fortuna45/NetgameGaming.png"),
  netgameP: require("../../../assets/img/catalog/fortuna45/NetgameGaming.png"),
  "nolimitcity-gh": require("../../../assets/img/catalog/fortuna45/NolimitCityGaming.png"),
  "novo-matic": require("../../../assets/img/catalog/fortuna45/NovomaticGaming.png"),
  onegame: require("../../../assets/img/catalog/fortuna45/OneGame.png"),
  onetouch: require("../../../assets/img/catalog/fortuna45/OnetouchGaming.png"),
  pascalgaming: require("../../../assets/img/catalog/fortuna45/PascalGaming.png"),
  pateplay: require("../../../assets/img/catalog/fortuna45/PatePlayGaming.png"),
  phoenix7: require("../../../assets/img/catalog/fortuna45/Phoenix7_w.png"),
  "pixmove-games": require("../../../assets/img/catalog/fortuna45/PixmoveGaming.png"),
  platingaming: require("../../../assets/img/catalog/fortuna45/PlatinGaming_w.png"),
  platipus: require("../../../assets/img/catalog/fortuna45/PlatipusGaming.png"),
  "play-son": require("../../../assets/img/catalog/fortuna45/playson.png"),
  "play-sonP": require("../../../assets/img/catalog/fortuna45/PlaysonGaming.png"),
  popiplay: require("../../../assets/img/catalog/fortuna45/PopiplayGaming.png"),
  popok: require("../../../assets/img/catalog/fortuna45/PopOkGaming.png"),
  prospectgaming: require("../../../assets/img/catalog/fortuna45/ProspectGames.png"),
  redrake: require("../../../assets/img/catalog/fortuna45/RedRakeGaming.png"),
  redstone: require("../../../assets/img/catalog/fortuna45/RedstoneGaming.png"),
  "redtiger-gh": require("../../../assets/img/catalog/fortuna45/RedTigerGaming.png"),
  retrogames: require("../../../assets/img/catalog/fortuna45/RetroGaming.png"),
  rubyplay: require("../../../assets/img/catalog/fortuna45/RubyPlayGaming.png"),
  skywind: require("../../../assets/img/catalog/fortuna45/SkyWindgroupGaming.png"),
  smartsoft: require("../../../assets/img/catalog/fortuna45/Smartsoft_Gaming_bw.png"),
  spearhead: require("../../../assets/img/catalog/fortuna45/SpearheadGaming.png"),
  spearheadP: require("../../../assets/img/catalog/fortuna45/Spearhead_bw.png"),
  spinomenal: require("../../../assets/img/catalog/fortuna45/Spinomenal_w.png"),
  tada: require("../../../assets/img/catalog/fortuna45/TaDaGaming.png"),
  tiptop: require("../../../assets/img/catalog/fortuna45/TipTopGaming.png"),
  "turbo-games": require("../../../assets/img/catalog/fortuna45/TurboGamesGaming.png"),
  voltentertainment: require("../../../assets/img/catalog/fortuna45/VoltEntertainment.png"),
  "wazdan-a": require("../../../assets/img/catalog/fortuna45/WazdanGaming.png"),
  "yggdrasil-a": require("../../../assets/img/catalog/fortuna45/YggdrasilGaming_bw.png"),
  "intouch-games": require("../../../assets/img/catalog/fortuna45/intouch-games.png"),
  "only-play": require("../../../assets/img/catalog/fortuna45/only-play.png"),

  absolute: require("../../../assets/img/catalog/fortuna45/AbsoluteGaming.png"),
  "amusnet-live": require("../../../assets/img/catalog/fortuna45/AmusnetGaming.png"),
  creedroomz: require("../../../assets/img/catalog/fortuna45/CreedroomzGaming.png"),
  creedroomzP: require("../../../assets/img/catalog/fortuna45/CreedroomzGaming.png"),
  "creedroomz-b": require("../../../assets/img/catalog/fortuna45/CreedroomzGaming.png"),
  eclipse: require("../../../assets/img/catalog/fortuna45/eclipse.png"),
  "eclipse-b": require("../../../assets/img/catalog/fortuna45/eclipse.png"),
  eclipseP: require("../../../assets/img/catalog/fortuna45/eclipse.png"),
  eezecasino: require("../../../assets/img/catalog/fortuna45/Eeze_casinoGaming.png"),
  evolutionGH: require("../../../assets/img/catalog/fortuna45/EvolutionGaming.png"),
  evolutionGHHS: require("../../../assets/img/catalog/fortuna45/EvolutionGaming.png"),
  evolutionGHLS: require("../../../assets/img/catalog/fortuna45/EvolutionGaming.png"),
  evolutionGHVHS: require("../../../assets/img/catalog/fortuna45/EvolutionGaming.png"),
  evolutionGHX: require("../../../assets/img/catalog/fortuna45/EvolutionGaming.png"),
  evolutionGHY: require("../../../assets/img/catalog/fortuna45/EvolutionGaming.png"),
  ezugiX: require("../../../assets/img/catalog/fortuna45/EzugiGaming.png"),
  ezugiZ: require("../../../assets/img/catalog/fortuna45/EzugiGaming.png"),
  ftv: require("../../../assets/img/catalog/fortuna45/ftv.png"),
  "ftv-b": require("../../../assets/img/catalog/fortuna45/ftv.png"),
  ftvP: require("../../../assets/img/catalog/fortuna45/ftv.png"),
  iconic21: require("../../../assets/img/catalog/fortuna45/Iconic21Gaming.png"),
  "imagine live": require("../../../assets/img/catalog/fortuna45/ImaginLiveGaming.png"),
  imagineliveHS: require("../../../assets/img/catalog/fortuna45/ImaginLiveGaming.png"),
  imagineliveLS: require("../../../assets/img/catalog/fortuna45/ImaginLiveGaming.png"),
  livegames: require("../../../assets/img/catalog/fortuna45/LiveGames.png"),
  "micro-gaming-live": require("../../../assets/img/catalog/fortuna45/microgaming.png"),
  "onetouch-bj": require("../../../assets/img/catalog/fortuna45/OnetouchGaming.png"),
  "onetouch-live": require("../../../assets/img/catalog/fortuna45/OnetouchGaming.png"),
  "pateplay-live": require("../../../assets/img/catalog/fortuna45/PatePlayGaming.png"),
  "platipus-live": require("../../../assets/img/catalog/fortuna45/PlatipusGaming.png"),
  "popok-live": require("../../../assets/img/catalog/fortuna45/PopOkGaming.png"),
  "pragmatic-bj": require("../../../assets/img/catalog/fortuna45/pragmatic.png"),
  "pragmatic-bj2": require("../../../assets/img/catalog/fortuna45/pragmatic.png"),
  "pragmatic-live": require("../../../assets/img/catalog/fortuna45/pragmatic.png"),
  religa: require("../../../assets/img/catalog/fortuna45/ReligaGaming.png"),
  sagaming: require("../../../assets/img/catalog/fortuna45/SAGaming.png"),
  sexygaming: require("../../../assets/img/catalog/fortuna45/sexygaming.png"),
  "skywind-bj": require("../../../assets/img/catalog/fortuna45/SkyWindgroupGaming.png"),
  "skywind-live": require("../../../assets/img/catalog/fortuna45/SkyWindgroupGaming.png"),
  tvbet: require("../../../assets/img/catalog/fortuna45/TvBetGaming.png"),
  vision: require("../../../assets/img/catalog/fortuna45/vision.png"),
  "vision-b": require("../../../assets/img/catalog/fortuna45/vision.png"),
  visionP: require("../../../assets/img/catalog/fortuna45/vision.png"),
  "7-mojos": require("../../../assets/img/catalog/fortuna45/7mojos.png"),

  vivolive: require("../../../assets/img/catalog/fortuna45/vivo.png"),

  GASEA_7Mojos: require("../../../assets/img/catalog/fortuna45/7mojosGaming.png"),
  GASEA_AA_Sexy: require("../../../assets/img/catalog/fortuna45/sexygaming.png"),
  //"GASEA_Big_Gaming": require("../../../assets/img/catalog/fortuna45/Big_Gaming.png"),
  GASEA_CQ9: require("../../../assets/img/catalog/fortuna45/cq9.png"),
  //"GASEA_Coockfight6": require("../../../assets/img/catalog/fortuna45/Coockfight6.png"),
  //"GASEA_Crystal": require("../../../assets/img/catalog/fortuna45/Crystal.png"),
  GASEA_Evolution_Live: require("../../../assets/img/catalog/fortuna45/evolution_gaming.png"),
  GASEA_Iconic21: require("../../../assets/img/catalog/fortuna45/Iconic21Gaming.png"),
  GASEA_Microgaming: require("../../../assets/img/catalog/fortuna45/microgaming.png"),
  GASEA_YeeBet: require("../../../assets/img/catalog/fortuna45/yeebet.png"),
  GASEA_Pragmatic_Play: require("../../../assets/img/catalog/fortuna45/pragmatic.png"),
  //"GASEA_Winfinity": require("../../../assets/img/catalog/fortuna45/WinfinityGaming.png"),

  GASEA_Big_Time_Gaming: require("../../../assets/img/catalog/fortuna45/BigTimeGaming.png"),
  GASEA_3Oaks: require("../../../assets/img/catalog/fortuna45/3Oaks_3.png"),
  GASEA_Netent: require("../../../assets/img/catalog/fortuna45/NetEntGaming.png"),
  GASEA_NoLimit_City: require("../../../assets/img/catalog/fortuna45/NolimitCityGaming.png"),
  GASEA_Playson: require("../../../assets/img/catalog/fortuna45/PlaysonGaming.png"),
  GASEA_Red_Tiger: require("../../../assets/img/catalog/fortuna45/RedTigerGaming.png"),
  GASEA_Ruby_Play: require("../../../assets/img/catalog/fortuna45/RubyPlayGaming.png"),
  GASEA_YellowBat: require("../../../assets/img/catalog/gasea/YellowBatGaming.png"),
  GASEA_Vplus: require("../../../assets/img/catalog/gasea/vplus.png"),
  GASEA_Thunderkick: require("../../../assets/img/catalog/gasea/GASEA_Thunderkick.png"),
  GASEA_The_Better_Platform: require("../../../assets/img/catalog/gasea/The_Better_Platform.png"),
  GASEA_Relax_Gaming: require("../../../assets/img/catalog/gasea/Relax_Gaming.png"),
  GASEA_KingMidas: require("../../../assets/img/catalog/gasea/kingmidas.png"),
  GASEA_JDB: require("../../../assets/img/catalog/gasea/JDB.png"),
  GASEA_GTF: require("../../../assets/img/catalog/gasea/GTF.png"),
  GASEA_PlayNGo: require("../../../assets/img/catalog/gasea/PlayNGo.png"),
  GASEA_Lets_Go: require("../../../assets/img/catalog/gasea/Lets_Go.png"),
  GASEA_DB_Slots: require("../../../assets/img/catalog/gasea/DB_Slots.png"),
  GASEA_CP_Games: require("../../../assets/img/catalog/gasea/CP_Games.png"),
  GASEA_CG: require("../../../assets/img/catalog/gasea/CG.png"),
  GASEA_BNG: require("../../../assets/img/catalog/gasea/BNG.png"),
  GASEA_AvatarUX: require("../../../assets/img/catalog/gasea/AvatarUX.png"),
  GASEA_Alize_Slots: require("../../../assets/img/catalog/gasea/Alize_Slots.png"),
  GASEA_AdvantPlay: require("../../../assets/img/catalog/gasea/AdvantPlay.png"),

  GASEA_Big_Gaming: require("../../../assets/img/catalog/gasea/Big_Gaming.png"),
  GASEA_Cockfight6: require("../../../assets/img/catalog/gasea/Cockfight6.png"),
  GASEA_Crystal: require("../../../assets/img/catalog/gasea/Crystal.png"),
  GASEA_Winfinity: require("../../../assets/img/catalog/gasea/WinfinityGaming.png"),
  GASEA_Habanero: require("../../../assets/img/catalog/gasea/habanero.png"),
  GASEA_KY_Poker: require("../../../assets/img/catalog/gasea/KY_Poker.png"),
  GASEA_Tada: require("../../../assets/img/catalog/fortuna45/TaDaGaming.png"),

  amatic: require("../../../assets/img/catalog/gasea/amatic.png"),
  vivolive: require("../../../assets/img/catalog/fortuna45/vivo.png"),
  "yoriginal-games": require("../../../assets/img/catalog/gasea/yoriginal-games.png"),
  "in-out": require("../../../assets/img/catalog/gasea/in-out.png"),
  GASEA_Habanero: require("../../../assets/img/catalog/gasea/habanero.png"),
  GASEA_KY_Poker: require("../../../assets/img/catalog/gasea/KY_Poker.png"),
  elcasino: require("../../../assets/img/catalog/gasea/elcasino.png"),
  /* "aviatrix": require("../../../assets/img/catalog/fortuna45/evolution_gaming.png"),
    
    "dreamgaming": require("../../../assets/img/catalog/fortuna45/evolution_gaming.png"),
    "fatpanda": require("../../../assets/img/catalog/fortuna45/evolution_gaming.png"),
    "reelkingdom": require("../../../assets/img/catalog/fortuna45/evolution_gaming.png"),
    "playson": require("../../../assets/img/catalog/fortuna45/evolution_gaming.png"),
  */
};
